import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { BaseSiteService, CmsService, GlobalMessageService, ProductService, TranslationService } from '@spartacus/core';
import { BREAKPOINT, BreakpointService } from '@spartacus/storefront';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Subscription, auditTime, switchMap, tap } from 'rxjs';
import { browserRefresh } from 'src/app/app.component';
import {
  INVOICE_HEADERS,
  INVOICE_SORT_MAPPING,
  INVOICE_SORT_OPTIONS,
  OrderView,
} from 'src/app/model/order-history.model';
import { BaseStoreService } from 'src/app/service/base-store/base-store.service';
import { CheckoutServiceExtends } from 'src/app/service/checkout/checkout.service';
import { ComponentService } from 'src/app/service/component.service';
import { ShimanoCountryService } from 'src/app/service/country/shimano-country.service';
import { ShimanoLanguageService } from 'src/app/service/language/shimano-language.service';
import { MyaccountPersonalInfoService } from 'src/app/service/myaccount/myaccount-personal-info.service';
import { GetInvoicesPayload, OrderHistoryService } from 'src/app/service/orders/order-history.service';
import { CustomProductService } from 'src/app/service/product/custom-product.service';
import { StorageService } from 'src/app/service/storage/storage.service';
import { CurrentBusinessType } from 'src/app/utility/current-businesstype';
import { OrderHistoryComponent } from '../order-history.component';

@Component({
  selector: 'app-order-history-invoice',
  templateUrl: './order-history-invoice.component.html',
  styleUrls: ['../order-history.component.scss', './order-history-invoice.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class OrderHistoryInvoiceComponent extends OrderHistoryComponent implements OnInit, OnDestroy {
  invoiceHistoryResults: any[] = [];
  invoiceListLoading: boolean = true;
  infiniteScrollInProgress: boolean;
  invoiceHeaders = INVOICE_HEADERS;
  invoiceHistoryPayload: GetInvoicesPayload = {
    currentPage: 0,
    dir: 'desc',
    fields: 'DEFAULT',
    filterSearchData: '',
    filterSearchBy: '',
    orderStatus: [],
    orderTypes: [],
    pageSize: 12,
    sort: 'invoiceDate',
    fromDate: '',
    toDate: '',
  };

  @Input() currentPayload: any;
  @Output() toggleSidenavEvent = new EventEmitter<boolean>();
  @Output() updateSortSelectedOptionEvent = new EventEmitter<string>();

  // Subscriptions
  getInvoiceHistorySubs$: Subscription;
  sortChangeSubs$: Subscription;

  constructor(
    cms: CmsService,
    orderHistoryService: OrderHistoryService,
    componentService: ComponentService,
    cdr: ChangeDetectorRef,
    productService: ProductService,
    baseStoreService: BaseStoreService,
    baseService: BaseSiteService,
    customProductService: CustomProductService,
    translationService: TranslationService,
    countryService: ShimanoCountryService,
    checkoutServiceExtends: CheckoutServiceExtends,
    breakpointService: BreakpointService,
    languageService: ShimanoLanguageService,
    currentBusinesstype: CurrentBusinessType,
    deviceDetectorService: DeviceDetectorService,
    router: ActivatedRoute,
    storageService: StorageService,
    messageService: GlobalMessageService,
    myAccountPersonalInforService: MyaccountPersonalInfoService,
    renderer: Renderer2,
    elementRef: ElementRef
  ) {
    super(
      cms,
      orderHistoryService,
      componentService,
      cdr,
      productService,
      baseStoreService,
      baseService,
      customProductService,
      translationService,
      countryService,
      checkoutServiceExtends,
      breakpointService,
      languageService,
      currentBusinesstype,
      deviceDetectorService,
      router,
      storageService,
      messageService,
      myAccountPersonalInforService,
      renderer,
      elementRef
    );
    this.storageService.setSessionStorageItem('orderState', OrderView.INVOICE);
  }

  ngOnInit(): void {
    this.orderView = OrderView.INVOICE;
    this.mode = new FormControl('over');

    this.triggerOrdersSubscription$ = this.orderHistoryService.triggerOrders$.subscribe((status) => {
      if (status) {
        this.orderHistoryService.getInvoiceHistory$.next([false]);
      }
    });

    //set orderState value
    this.browserRefresh = browserRefresh;
    if (this.browserRefresh) {
      this.storageService.setSessionStorageItem('orderState', OrderView.INVOICE);
    }

    this.getInitSrpToggleValue();

    this.srpSubscription$ = this.componentService.srpToggle$.subscribe((checked) => {
      this.showSrp = checked;
      this.cdr.detectChanges();
    });

    this.filterSearchBySubscription$ = this.orderHistoryService.filterSearchBy$.subscribe((filterSearchBy) => {
      if (this.invoiceHistoryPayload.filterSearchBy !== filterSearchBy) {
        this.filterSearchBy = filterSearchBy;
        this.invoiceHistoryPayload.filterSearchBy = filterSearchBy;
        this.filterSearch = null;
      }
    });

    this.filterSearchSubscription$ = this.orderHistoryService.filterSearch$.subscribe((filterSearch) => {
      if (this.filterSearch !== filterSearch) {
        this.filterSearch = filterSearch;
        this.invoiceHistoryPayload.filterSearchData = filterSearch;
        this.orderHistoryService.getInvoiceHistory$.next([false]);
        this.cdr.detectChanges();
      }
    });

    this.setOrderStatusSubscription$ = this.orderHistoryService.setOrderStatus$.subscribe((status) => {
      if (!this.invoiceHistoryPayload.orderStatus.includes(status)) {
        this.invoiceHistoryPayload.orderStatus.push(status);
      }
      this.orderHistoryService.getInvoiceHistory$.next([false]);
      this.cdr.detectChanges();
    });

    this.setOrderTypeSubscription$ = this.orderHistoryService.setOrderType$.subscribe((orderTypes) => {
      this.invoiceHistoryPayload.orderTypes = orderTypes;
      this.orderHistoryService.getInvoiceHistory$.next([false]);
      this.cdr.detectChanges();
    });

    this.removeOrderStatusSubscription$ = this.orderHistoryService.removeOrderStatus$.subscribe((status) => {
      this.invoiceHistoryPayload.orderStatus = this.invoiceHistoryPayload.orderStatus.filter(
        (item: any) => item !== status
      );
      this.orderHistoryService.getInvoiceHistory$.next([false]);
      this.cdr.detectChanges();
    });

    this.fromDateSubscription$ = this.orderHistoryService.fromDate$.subscribe((date) => {
      this.invoiceHistoryPayload.fromDate = date;
      this.orderHistoryService.getInvoiceHistory$.next([false]);
      this.cdr.detectChanges();
    });

    this.toDateSubscription$ = this.orderHistoryService.toDate$.subscribe((date) => {
      this.invoiceHistoryPayload.toDate = date;
      this.orderHistoryService.getInvoiceHistory$.next([false]);
      this.cdr.detectChanges();
    });

    this.selectedFilterSubscription$ = this.orderHistoryService.selectedFilter$.subscribe((status) => {
      this.invoiceHistoryPayload.currentPage = 0;
      this.selectedFilter = status;
      this.cdr.detectChanges();
    });

    this.sortChangeSubs$ = this.orderHistoryService.sortChange$.subscribe((sort: string) => {
      this.invoiceSort = sort;
      [this.invoiceHistoryPayload.sort, this.invoiceHistoryPayload.dir] = INVOICE_SORT_MAPPING[sort].split(':');
      this.orderHistoryService.getInvoiceHistory$.next([false]);
      this.updateSortSelectedOptionEvent.emit(sort);
    });

    this.clearFilterSubscription$ = this.orderHistoryService.clearFilters$.subscribe((clear) => {
      this.invoiceHistoryPayload.dir = 'desc';
      this.invoiceHistoryPayload.filterSearchData = '';
      this.invoiceHistoryPayload.filterSearchBy = '';
      this.invoiceHistoryPayload.fromDate = '';
      this.invoiceHistoryPayload.orderStatus = [];
      this.invoiceHistoryPayload.orderTypes = [];
      this.invoiceHistoryPayload.sort = 'invoiceDate';
      this.invoiceSort = INVOICE_SORT_OPTIONS[0].code;
      this.updateSortSelectedOptionEvent.emit(this.invoiceSort);
      this.invoiceHistoryPayload.toDate = '';
      this.orderHistoryService.getInvoiceHistory$.next([false]);
      this.cdr.detectChanges();
    });

    this.businessType = this.currentBusinesstype.getBusinessType();

    this.totalOrdersSubscription$ = this.orderHistoryService.totalOrders$.subscribe((total) => {
      this.totalNumberOfResults = total;
      this.cdr.detectChanges();
    });

    this.breakpointSubscription$ = this.breakpointService.breakpoint$.subscribe((breakpoint) => {
      const dropdownBackdropElement = document.getElementsByClassName('cdk-overlay-backdrop')[0] as HTMLElement;
      this.isDesktop = breakpoint === BREAKPOINT.xl;
      if (dropdownBackdropElement) {
        dropdownBackdropElement.click();
      }
      if (breakpoint === BREAKPOINT.xl) {
        this.sideNavOpened = false;
        this.orderHistoryService.setTotalOrders(this.totalNumberOfResults);
      }
    });

    this.reloadLanguageForOrderTypeSubscription$ = this.activeLanguage$.subscribe((language) => {
      if (this.currentLanguage !== language) {
        this.currentLanguage = language;
        this.orderHistoryService.getInvoiceHistory$.next([false]);
      }
      this.cdr.detectChanges();
    });

    this.updateSortSelectedOptionEvent.emit(INVOICE_SORT_OPTIONS[0].code);
    this.transferCurrentPayload();
    this.getInvoices();
  }

  getInvoices(): void {
    this.getInvoiceHistorySubs$ = this.orderHistoryService.getInvoiceHistory$
      .pipe(
        auditTime(1000),
        tap(([onScroll]: [boolean]) => {
          if (!onScroll) {
            this.invoiceHistoryPayload.currentPage = 0;
            this.invoiceHistoryResults = [];
            this.invoiceListLoading = true;
          }
        }),
        switchMap(() => this.orderHistoryService.getInvoices(this.invoiceHistoryPayload))
      )
      .subscribe({
        next: (res) => {
          this.setInvoiceHistoryResults(res);
        },
        error: (err) => {
          this.setError();
          this.cdr.detectChanges();
        },
      });
  }

  setInvoiceHistoryResults(items: any): void {
    this.hasData = Object.keys(items).length;
    this.invoiceListLoading = false;

    if (this.hasData !== 0) {
      this.updatePaginationModel(items);
      this.orderHistoryService.setTotalOrders(items.pagination.totalNumberOfResults);
      items.results.forEach((result: any) => {
        this.invoiceHistoryResults.push(result);
      });
    } else {
      this.orderHistoryService.setTotalOrders(this.hasData);
    }

    this.infiniteScrollInProgress = false;
    this.cdr.detectChanges();
  }

  onScroll() {
    const nextPage = this.invoiceHistoryPayload.currentPage + 1;
    this.infiniteScrollInProgress = true;
    if (nextPage < this.PaginationModel.totalPages) {
      this.invoiceHistoryPayload.currentPage = nextPage;
      this.orderHistoryService.getInvoiceHistory$.next([true]);
    } else {
      this.infiniteScrollInProgress = false;
    }
  }

  transferCurrentPayload(): void {
    const keysToTransfer = ['filterSearchData', 'filterSearchBy', 'orderStatus', 'orderTypes', 'fromDate', 'toDate'];

    keysToTransfer.forEach((key) => {
      if (this.currentPayload.hasOwnProperty(key)) {
        (this.invoiceHistoryPayload as any)[key] = this.currentPayload[key];
      }
    });
  }

  toggleSidenav(): void {
    this.toggleSidenavEvent.emit(true);
  }

  downloadPdf($event: any, url: string): void {
    $event.stopPropagation();
    const link = document.createElement('a');
    link.href = url + '&download=true';
    link.click();
  }

  openPdf(url: string): void {
    window.open(url, '_blank');
  }

  exportCsv(): void {
    const endpoint = 'exportInvoiceHistory';
    const fileName = 'Invoices';
    this.orderHistoryService.exportCsv(this.invoiceHistoryPayload, endpoint, fileName).subscribe();
  }

  setError(): void {
    this.orderHistoryService.setTotalOrders(0);
    this.invoiceListLoading = false;
    this.infiniteScrollInProgress = false;
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    this.getInvoiceHistorySubs$?.unsubscribe();
    this.sortChangeSubs$?.unsubscribe();
  }
}
